import { Button, Grid } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import { EntityOwnershipCard } from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { CopyMarkdown } from '../techdocs/CopyMarkdownAddon';

import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';

import {
  EntityAdrContent,
  isAdrAvailable,
} from '@backstage-community/plugin-adr';
import { EntityTechInsightsScorecardCard } from '@backstage-community/plugin-tech-insights';
import { FeatureFlagged } from '@backstage/core-app-api';
import { CensusEntityTab } from '@internal/backstage-plugin-census';
import {
  EntityTeamRosterSection,
  EntityTeamCompositionCard,
  EntityWorksOnBehalfOfCard,
  EntityTeamFlagsCard,
  EntityTeamIdentifierCard,
  EntityTeamRelationsGraphCard,
  EntityTeamLinksCard,
  EntityUserHero,
  EntityUserTeamsCard,
  EntityUserDirectReportsCard,
} from '@internal/backstage-plugin-team-management-frontend';
import { EntityGroupSecurityTab } from '@internal/backstage-plugin-security-insights';
import { EntityGroupOkrTab } from '@internal/backstage-plugin-okr-frontend';
import { EntityRosterVerificationBanner } from '@internal/backstage-plugin-slack-nudges';
import {
  EntityComponentScorecardsCard,
  EntityScorecardsTab,
  ScorecardsSummaryCard,
} from '@internal/backstage-plugin-scorecard';
import { OrcaSecurityTab } from '../orca/OrcaSecurityTab';
import { OrcaAlertsCard } from '../orca/OrcaAlertsCard';
import { GroupProfileCardWithoutEdit } from './GroupProfileCardWithoutEdit';
import {
  DatadogSummaryCard,
  DatadogEntityTab,
  DatadogDataProvider,
  isDatadogAvailable,
} from '@internal/backstage-plugin-datadog';
import {
  CicdPromoterEntityTab,
  isCicdPromoterAvailable,
} from '@internal/backstage-plugin-cicd-promoter';
import { AllureReportContent } from '../allure/AllureReportContent';
import { isAllureTabAvailable } from '../allure/isAllureTabAvailable';
import { useAsyncEntity, useEntity } from '@backstage/plugin-catalog-react';
import { useNavigate, useLocation } from 'react-router-dom';
import StorefrontIcon from '@material-ui/icons/Storefront';
import EditIcon from '@material-ui/icons/Edit';
import ArchiveIcon from '@material-ui/icons/Archive';
import { useEffect, useRef, useState, ReactNode } from 'react';
import {
  configApiRef,
  featureFlagsApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useMixpanelAnalytics, getLastInAppPath } from '../../lib/analytics';
import { fade, makeStyles } from '@material-ui/core/styles';
import { BackstageTheme } from '@backstage/theme';

// Backstage uses a counter-based JSS class generator (e.g. MuiTableCell-root-964),
// so stable MUI class names (.MuiTableCell-root) don't exist at runtime.
// Target td/th directly instead.
const useKubernetesTabStyles = makeStyles<BackstageTheme>({
  root: {
    '& td, & th': {
      padding: '4px 8px',
      fontSize: '0.8rem',
    },
  },
});

const KubernetesTabWrapper = ({ children }: { children: ReactNode }) => {
  const classes = useKubernetesTabStyles();
  return <div className={classes.root}>{children}</div>;
};

const useScorecardWrapperStyles = makeStyles(() => ({
  root: {
    '& [class*="MuiAccordion-root"]': {
      backgroundColor: 'var(--bui-bg-neutral-1)',
    },
  },
}));

const ScorecardStyleWrapper = ({ children }: { children: ReactNode }) => {
  const classes = useScorecardWrapperStyles();
  return <div className={classes.root}>{children}</div>;
};

// Generic Tech Insights widget; replaced on Component pages by
// `EntityComponentScorecardsCard` when `scorecard-v2` is on (PAT-899).
const techInsightsScorecardCard = (
  <Grid item xs={12} md={6}>
    <ScorecardStyleWrapper>
      <EntityTechInsightsScorecardCard
        title="Scorecard"
        checksId={['portfolioLinterPassing']}
      />
    </ScorecardStyleWrapper>
  </Grid>
);

const componentScorecardCard = (
  <Grid item xs={12} md={6}>
    <EntityComponentScorecardsCard />
  </Grid>
);

// Edit/Archive on the Group page are a "safe vs. destructive" pair: Edit is the
// filled primary action, Archive an outlined destructive one. This theme has no
// standard MUI `palette.error` red for the destructive tone (`color="secondary"`
// renders brand pink, #FF33BB), so reuse the same red the rest of the app uses
// for error state — palette.status.error (#E22134 in both light and dark).
const useTeamActionStyles = makeStyles<BackstageTheme>(theme => ({
  editButton: {
    marginRight: theme.spacing(1),
  },
  archiveButton: {
    borderColor: theme.palette.status.error,
    color: theme.palette.status.error,
    '&:hover': {
      borderColor: theme.palette.status.error,
      backgroundColor: fade(theme.palette.status.error, 0.08),
    },
    '&:focus-visible': {
      borderColor: theme.palette.status.error,
    },
  },
}));

// Wraps children in DatadogDataProvider only when the `datadog` feature flag
// is enabled, so disabled users don't trigger Datadog backend requests.
const MaybeDatadogProvider = ({ children }: { children: ReactNode }) => {
  const flags = useApi(featureFlagsApiRef);
  if (!flags.isActive('datadog')) return <>{children}</>;
  return <DatadogDataProvider>{children}</DatadogDataProvider>;
};

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
      <CopyMarkdown />
      <Mermaid />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const cicdContent = (
  // This is an example of how you can implement your company's logic in entity page.
  // You can for example enforce that all components of type 'service' should use GitHubActions
  <EntitySwitch>
    {/*
      Here you can add support for different CI/CD services, for example
      using @backstage-community/plugin-github-actions as follows:
      <EntitySwitch.Case if={isGithubActionsAvailable}>
        <EntityGithubActionsContent />
      </EntitySwitch.Case>
     */}
    <EntitySwitch.Case>
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}

    <Grid item md={6}>
      <EntityAboutCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard height={400} />
    </Grid>

    <Grid item md={4} xs={12}>
      <EntityLinksCard />
    </Grid>
    <Grid item md={8} xs={12}>
      <EntityHasSubcomponentsCard />
    </Grid>

    {/* `overviewContent` is also reached via `defaultEntityPage` for non-Component kinds, which keep the generic widget. */}
    <EntitySwitch>
      <EntitySwitch.Case if={isKind('component')}>
        <FeatureFlagged without="scorecard-v2">
          {techInsightsScorecardCard}
        </FeatureFlagged>
        <FeatureFlagged with="scorecard-v2">
          {componentScorecardCard}
        </FeatureFlagged>
      </EntitySwitch.Case>
      <EntitySwitch.Case>{techInsightsScorecardCard}</EntitySwitch.Case>
    </EntitySwitch>

    <FeatureFlagged with="datadog">
      <EntitySwitch>
        <EntitySwitch.Case if={isDatadogAvailable}>
          <Grid item xs={12} md={6}>
            <DatadogSummaryCard />
          </Grid>
        </EntitySwitch.Case>
      </EntitySwitch>
    </FeatureFlagged>

    <FeatureFlagged with="orca">
      <Grid item xs={12} md={6}>
        <OrcaAlertsCard />
      </Grid>
    </FeatureFlagged>
  </Grid>
);

// Only shows for Components annotated `qameta.io/allure-project`. Used by the service, website and default
// page types: test repos are typically `library`, which falls through to the default page.
const allureReportTab = (
  <EntityLayout.Route
    if={isAllureTabAvailable}
    path="/allure"
    title="Allure Report"
  >
    <AllureReportContent />
  </EntityLayout.Route>
);

const serviceEntityPage = (
  <MaybeDatadogProvider>
    <EntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        {overviewContent}
      </EntityLayout.Route>

      <EntityLayout.Route path="/ci-cd" title="CI/CD">
        {cicdContent}
      </EntityLayout.Route>

      <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADRs">
        <EntityAdrContent />
      </EntityLayout.Route>

      <FeatureFlagged with="kubernetes">
        <EntityLayout.Route
          path="/kubernetes"
          title="Kubernetes"
          if={isKubernetesAvailable}
        >
          <KubernetesTabWrapper>
            <EntityKubernetesContent />
          </KubernetesTabWrapper>
        </EntityLayout.Route>
      </FeatureFlagged>

      <EntityLayout.Route path="/api" title="API">
        <Grid container spacing={3} alignItems="stretch">
          <Grid item md={6}>
            <EntityProvidedApisCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumedApisCard />
          </Grid>
        </Grid>
      </EntityLayout.Route>

      <EntityLayout.Route path="/dependencies" title="Dependencies">
        <Grid container spacing={3} alignItems="stretch">
          <Grid item md={6}>
            <EntityDependsOnComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityDependsOnResourcesCard />
          </Grid>
        </Grid>
      </EntityLayout.Route>

      <EntityLayout.Route path="/docs" title="Docs">
        {techdocsContent}
      </EntityLayout.Route>

      <FeatureFlagged with="fleet-census">
        <EntityLayout.Route path="/census" title="Census">
          <CensusEntityTab />
        </EntityLayout.Route>
      </FeatureFlagged>

      <FeatureFlagged with="orca">
        <EntityLayout.Route path="/orca" title="Orca">
          <OrcaSecurityTab />
        </EntityLayout.Route>
      </FeatureFlagged>

      <FeatureFlagged with="datadog">
        <EntityLayout.Route
          if={isDatadogAvailable}
          path="/datadog"
          title="Datadog"
        >
          <DatadogEntityTab />
        </EntityLayout.Route>
      </FeatureFlagged>

      <FeatureFlagged with="cicd-promoter">
        <EntityLayout.Route
          if={isCicdPromoterAvailable}
          path="/cicd-promoter"
          title="Promotions"
        >
          <CicdPromoterEntityTab />
        </EntityLayout.Route>
      </FeatureFlagged>

      {allureReportTab}
    </EntityLayout>
  </MaybeDatadogProvider>
);

const websiteEntityPage = (
  <MaybeDatadogProvider>
    <EntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        {overviewContent}
      </EntityLayout.Route>

      <EntityLayout.Route path="/ci-cd" title="CI/CD">
        {cicdContent}
      </EntityLayout.Route>

      <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADRs">
        <EntityAdrContent />
      </EntityLayout.Route>

      <FeatureFlagged with="kubernetes">
        <EntityLayout.Route
          path="/kubernetes"
          title="Kubernetes"
          if={isKubernetesAvailable}
        >
          <KubernetesTabWrapper>
            <EntityKubernetesContent />
          </KubernetesTabWrapper>
        </EntityLayout.Route>
      </FeatureFlagged>

      <EntityLayout.Route path="/dependencies" title="Dependencies">
        <Grid container spacing={3} alignItems="stretch">
          <Grid item md={6}>
            <EntityDependsOnComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityDependsOnResourcesCard />
          </Grid>
        </Grid>
      </EntityLayout.Route>

      <EntityLayout.Route path="/docs" title="Docs">
        {techdocsContent}
      </EntityLayout.Route>

      <FeatureFlagged with="orca">
        <EntityLayout.Route path="/orca" title="Orca">
          <OrcaSecurityTab />
        </EntityLayout.Route>
      </FeatureFlagged>

      <FeatureFlagged with="datadog">
        <EntityLayout.Route
          if={isDatadogAvailable}
          path="/datadog"
          title="Datadog"
        >
          <DatadogEntityTab />
        </EntityLayout.Route>
      </FeatureFlagged>

      {allureReportTab}
    </EntityLayout>
  </MaybeDatadogProvider>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    {allureReportTab}
  </EntityLayout>
);

function EntityAnalyticsTracker() {
  // Mounted at the entityPage root so it stays active across all tabs and
  // direct loads, including before the entity is fully loaded. Use
  // useAsyncEntity() (not useEntity()) so the component does not throw
  // during loading.
  const { entity } = useAsyncEntity();
  const location = useLocation();
  const { track } = useMixpanelAnalytics();
  const prevTabRef = useRef<string>('');
  const prevEntityRefRef = useRef<string>('');

  useEffect(() => {
    if (!entity) return;
    const kind = entity.kind ?? '';
    const namespace = entity.metadata.namespace ?? 'default';
    const name = entity.metadata.name ?? '';
    const entityRef = `${kind.toLowerCase()}:${namespace}/${name}`;
    const spec = (entity.spec ?? {}) as Record<string, any>;

    // Derive source from the previous in-app pathname (tracked by
    // MixpanelAnalyticsApi). document.referrer reflects only the external
    // referrer in an SPA, so it would misclassify catalog-list navigations.
    const previousPath = getLastInAppPath();
    const isCatalogList =
      previousPath.startsWith('/catalog') &&
      previousPath.split('/').length <= 3;
    const source = isCatalogList ? 'catalog_list' : 'direct_link';

    // Extract current tab from location pathname (sub-path after entity path).
    const pathParts = location.pathname.split('/');
    const tabViewed = (pathParts.length > 5 ? pathParts[5] : '') || 'overview';

    track('catalog_entity_viewed', {
      entity_ref: entityRef,
      entity_kind: kind,
      entity_type: spec.type ?? '',
      entity_owner: spec.owner ?? '',
      entity_lifecycle: spec.lifecycle ?? '',
      tab_viewed: tabViewed,
      source,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.metadata.name, entity?.metadata.namespace, entity?.kind]);

  // Track tab changes
  useEffect(() => {
    if (!entity) return;
    const kind = entity.kind ?? '';
    const namespace = entity.metadata.namespace ?? 'default';
    const name = entity.metadata.name ?? '';
    const entityRef = `${kind.toLowerCase()}:${namespace}/${name}`;

    // Extract tab from location pathname (the sub-path after the entity path)
    const pathParts = location.pathname.split('/');
    const tabName = pathParts.length > 5 ? pathParts[5] : 'overview';
    const currentTab = tabName || 'overview';

    // Reset tab tracking when the entity changes — otherwise the new
    // entity's initial tab would be reported as a "change" from the
    // previous entity's last tab.
    if (prevEntityRefRef.current !== entityRef) {
      prevEntityRefRef.current = entityRef;
      prevTabRef.current = currentTab;
      return;
    }

    if (prevTabRef.current && prevTabRef.current !== currentTab) {
      track('catalog_entity_tab_changed', {
        entity_ref: entityRef,
        tab_name: currentTab,
        previous_tab: prevTabRef.current,
      });
    }
    prevTabRef.current = currentTab;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.pathname,
    entity?.metadata.name,
    entity?.metadata.namespace,
    entity?.kind,
  ]);

  return null;
}

function MarketplaceInstallButton() {
  const { entity } = useEntity();
  const navigate = useNavigate();
  const namespace = entity.metadata.namespace ?? 'default';
  return (
    <Button
      variant="contained"
      color="primary"
      startIcon={<StorefrontIcon />}
      onClick={() =>
        navigate(`/ai-marketplace/${namespace}/${entity.metadata.name}`)
      }
    >
      Installation Instructions
    </Button>
  );
}

const claudePluginEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item xs={12}>
          <MarketplaceInstallButton />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <FeatureFlagged without="scorecard-v2">
          {techInsightsScorecardCard}
        </FeatureFlagged>
        <FeatureFlagged with="scorecard-v2">
          {componentScorecardCard}
        </FeatureFlagged>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('claude-plugin')}>
      {claudePluginEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12}>
          <EntityUserHero />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityUserTeamsCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityUserDirectReportsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

// PAT-771: every level except the single root 'company' entity is editable
// through the team-management plugin — the backend's hierarchy validation and
// write routes already support all of these, this just controls which entity
// pages show the buttons.
const TEAM_ACTIONS_LEVELS = ['function', 'sub-function', 'xp', 'team'];

// Matches TeamsPage's LEVEL_LABELS (plugins/team-management-frontend), plus
// 'team' since the button copy needs a label for every level it can render
// on, not just the ones that get a badge.
const LEVEL_DISPLAY_LABELS: Record<string, string> = {
  function: 'Function',
  'sub-function': 'Sub-Function',
  xp: 'XP',
  team: 'Team',
};

/**
 * Keys the real implementation by entity name, forcing a full remount
 * (fresh canEdit=null) whenever the viewed entity changes — resetting
 * canEdit inside an effect isn't enough on its own, since the render
 * triggered by the entity change itself still happens first, with the
 * previous entity's canEdit value, before the effect gets a chance to run.
 */
function TeamActions() {
  const { entity } = useEntity();
  return <TeamActionsForEntity key={entity.metadata.name} />;
}

function TeamActionsForEntity() {
  const { entity } = useEntity();
  const navigate = useNavigate();
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const fetchApi = useApi(fetchApiRef);
  const classes = useTeamActionStyles();

  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | undefined>(
    undefined,
  );
  // null while the permission check is in flight — buttons stay hidden
  // rather than flashing enabled-then-disabled once the real answer arrives.
  const [canEdit, setCanEdit] = useState<boolean | null>(null);

  const { namespace = 'default', name } = entity.metadata;
  const backendBase = `${configApi.getString('backend.baseUrl')}/api/team-management`;

  useEffect(() => {
    let cancelled = false;
    // Reset before fetching — otherwise navigating from an authorized
    // group's page straight to an unauthorized one (without a full
    // remount) would keep the previous page's `true` visible until this
    // request resolves.
    if (namespace !== 'default') {
      setCanEdit(false);
      return undefined;
    }
    setCanEdit(null);
    (async () => {
      try {
        const response = await fetchApi.fetch(
          `${backendBase}/teams/${name}/permissions`,
        );
        const data = response.ok ? await response.json() : { canEdit: false };
        if (!cancelled) setCanEdit(Boolean(data.canEdit));
      } catch {
        if (!cancelled) setCanEdit(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, namespace]);

  // team/level is catalog-time normalized (GroupTypeFlagsProcessor) from
  // spec.type, including legacy pre-rename strings like 'business-unit' or
  // 'experience' that real DX-synced entities still carry — gate on it
  // rather than spec.type directly so those entities aren't missed. Absent
  // team/level defaults to 'team', matching the backend's normalizeGroupType.
  const level = entity.metadata.annotations?.['team/level'] ?? 'team';
  if (namespace !== 'default' || !TEAM_ACTIONS_LEVELS.includes(level)) {
    return null;
  }
  const levelLabel = LEVEL_DISPLAY_LABELS[level] ?? 'Team';

  const readOnly =
    configApi.getOptionalBoolean('teamManagement.readOnly') ?? false;
  const isArchived =
    entity.metadata.annotations?.['backstage.io/lifecycle'] === 'deprecated';

  const handleArchive = async () => {
    setArchiving(true);
    setArchiveError(undefined);
    try {
      const profile = await identityApi.getProfileInfo();
      const backstageIdentity = await identityApi.getBackstageIdentity();
      const response = await fetchApi.fetch(`${backendBase}/teams/${name}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          actorEntityRef: backstageIdentity.userEntityRef,
          actorEmail: profile.email ?? '',
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        setArchiveError(
          typeof data.error === 'string'
            ? data.error
            : (data.error?.message ?? 'Archive failed'),
        );
        return;
      }
      setShowArchiveDialog(false);
      navigate(`/catalog/${namespace}/group/${name}`);
    } catch (err) {
      setArchiveError(String(err));
    } finally {
      setArchiving(false);
    }
  };

  return (
    <>
      {isArchived && (
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          This {levelLabel.toLowerCase()} is archived. It no longer appears in
          the Teams view — this page remains reachable directly, for reference.
        </Alert>
      )}
      {/* Edit/Archive are gated on canEdit and !readOnly — the Archived
          banner above is neither: it's read-only status info that should
          stay visible to everyone, including in read-only deployments. */}
      {canEdit && !readOnly && (
        <>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/team-edit/${namespace}/group/${name}`)}
            className={classes.editButton}
          >
            Edit {levelLabel}
          </Button>
          {isArchived ? (
            <Tooltip
              title={`This ${levelLabel.toLowerCase()} is already archived`}
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ArchiveIcon />}
                  disabled
                >
                  Archive {levelLabel}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArchiveIcon />}
              className={classes.archiveButton}
              onClick={() => {
                setArchiveError(undefined);
                setShowArchiveDialog(true);
              }}
            >
              Archive {levelLabel}
            </Button>
          )}

          <Dialog
            open={showArchiveDialog}
            onClose={() => !archiving && setShowArchiveDialog(false)}
          >
            <DialogTitle>Archive {levelLabel}?</DialogTitle>
            <DialogContent>
              <Typography>
                This will archive the {levelLabel.toLowerCase()}. It cannot be
                undone. It will remain visible in the catalog but will be marked
                as deprecated.
              </Typography>
              {archiveError && (
                <Typography color="error" style={{ marginTop: 8 }}>
                  {archiveError}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setShowArchiveDialog(false)}
                disabled={archiving}
              >
                Cancel
              </Button>
              <Button
                color="secondary"
                onClick={handleArchive}
                disabled={archiving}
              >
                {archiving ? <CircularProgress size={16} /> : 'Archive'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
}

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12}>
          <EntityRosterVerificationBanner />
          <TeamActions />
        </Grid>
        <Grid item xs={12} md={6}>
          <GroupProfileCardWithoutEdit />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
        <Grid item xs={12}>
          <EntityTeamRosterSection />
        </Grid>
        <FeatureFlagged with="scorecard-v2">
          <Grid item xs={12}>
            <ScorecardsSummaryCard />
          </Grid>
        </FeatureFlagged>
        <EntityTeamCompositionCard />
        <EntityWorksOnBehalfOfCard />
        <Grid item xs={12} md={6}>
          <EntityTeamIdentifierCard />
        </Grid>
        <EntityTeamFlagsCard />
        <Grid item xs={12} md={6}>
          <EntityTeamLinksCard />
        </Grid>
        <Grid item xs={12}>
          <EntityTeamRelationsGraphCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/okrs" title="OKRs">
      <EntityGroupOkrTab />
    </EntityLayout.Route>
    <EntityLayout.Route path="/security" title="Security">
      <EntityGroupSecurityTab />
    </EntityLayout.Route>
    <FeatureFlagged with="scorecard-v2">
      <EntityLayout.Route path="/scorecards" title="Scorecards">
        <EntityScorecardsTab />
      </EntityLayout.Route>
    </FeatureFlagged>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <>
    {/*
      Tracker is mounted outside EntityLayout so it stays active across
      every tab (and on direct loads of non-overview tabs). Inside
      `overviewContent` it would only mount for `/`.
    */}
    <EntityAnalyticsTracker />
    <EntitySwitch>
      <EntitySwitch.Case if={isKind('component')} children={componentPage} />
      <EntitySwitch.Case if={isKind('api')} children={apiPage} />
      <EntitySwitch.Case if={isKind('group')} children={groupPage} />
      <EntitySwitch.Case if={isKind('user')} children={userPage} />
      <EntitySwitch.Case if={isKind('system')} children={systemPage} />
      <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

      <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
    </EntitySwitch>
  </>
);
